import {
  createContext, useCallback, useMemo, useState,
} from 'react';

interface LayoutContextProps {
  layout: JSX.Element,
  showSidebar: boolean,
  changeShowSidebar: (value: boolean) => void,
  addLayout: (layout: JSX.Element) => void,
  removeLayout: () => void,
}

export const LayoutContext = createContext<LayoutContextProps>({} as LayoutContextProps);
LayoutContext.displayName = 'LayoutContext';

interface Props {
  children: JSX.Element,
}

export const LayoutProvider = ({ children }: Props) => {
  const [layout, setLayout] = useState<JSX.Element | null>(null);
  const [showSidebar, setShowSidebar] = useState(true);

  const addLayout = useCallback((newLayout: JSX.Element) => {
    setLayout(newLayout);
  }, []);

  const changeShowSidebar = useCallback((newValue: boolean) => {
    setShowSidebar(newValue);
  }, []);

  const removeLayout = useCallback(() => {
    setLayout(null);
    changeShowSidebar(true);
  }, [changeShowSidebar]);

  const value = useMemo(() => ({
    layout,
    showSidebar,
    addLayout,
    removeLayout,
    changeShowSidebar,
  }), [layout, showSidebar, addLayout, removeLayout, changeShowSidebar]);

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};
